import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Typography,
  Paper,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tab,
  Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import { useCimbeo } from '../../context';
import ArtifactTypeSelector from '../../components/ArtifactTypeSelector';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`artifact-tabpanel-${index}`}
      aria-labelledby={`artifact-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function AddArtifactPopup({ open, onClose }) {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [newRequirement, setNewRequirement] = useState('');
  const [files, setFiles] = useState([]);
  const [artifactType, setArtifactType] = useState('requirements');
  
  const { 
    projectName, 
    projectId, 
    ingestedDocuments, 
    setIngestedDocuments, 
    projects, 
    setProjects 
  } = useCimbeo();

  const handleTabChange = (_, index) => {
    setTabValue(index);
  };

  const handleArtifactTypeChange = (event) => {
    setArtifactType(event.target.value);
  };

  const handleAddRequirement = () => {
    if (newRequirement.trim()) {
      setRequirements([...requirements, newRequirement.trim()]);
      setNewRequirement('');
    }
  };

  const handleDeleteRequirement = (index) => {
    const newRequirements = requirements.filter((_, i) => i !== index);
    setRequirements(newRequirements);
  };

  const onDrop = (acceptedFiles) => {
    setFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    }
  });

  const handleSubmit = () => {
    if (!projectName) {
      alert('Please select a project before ingesting documents');
      return;
    }

    // Create a new document for each file
    const newDocuments = files.map(file => ({
      id: Date.now() + Math.random(), // Ensure unique IDs
      name: file.name,
      type: artifactType,
      status: 'processing',
      completedSteps: [],
      projectName: projectName,
      timestamp: new Date().toISOString(),
      uploadedBy: "Brian Sisco"
    }));

    // Update the projects in context to include the new documents
    const updatedProjects = projects.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          documents: [...(project.documents || []), ...newDocuments.map(doc => doc.id)]
        };
      }
      return project;
    });

    // Update CIMBEO context
    setProjects(updatedProjects);
    setIngestedDocuments(prev => [...prev, ...newDocuments]);

    // Close the popup and navigate to the processing page
    onClose();
    navigate('/artifact-ingestion', { 
      state: { 
        selectedProject: {
          id: projectId,
          title: projectName
        },
        activeTab: 2  // This will indicate we want to show the processing tab
      }
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Add Artifact to {projectName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Paper sx={{ width: '100%', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              centered
            >
              <Tab label="File Upload" />
              <Tab label="Text Entry" />
            </Tabs>
          </Paper>

          <ArtifactTypeSelector 
            value={artifactType}
            onChange={handleArtifactTypeChange}
          />

          <TabPanel value={tabValue} index={0}>
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: '#FFFFFF',
              py: 2
            }}>
              <Paper
                {...getRootProps()}
                sx={{
                  width: '100%',
                  height: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderColor: isDragActive ? '#2B5587' : '#ccc',
                  backgroundColor: isDragActive ? 'rgba(43, 85, 135, 0.05)' : 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#2B5587',
                    backgroundColor: 'rgba(43, 85, 135, 0.05)',
                  },
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 48, color: '#2B5587', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive
                    ? 'Drop the files here'
                    : 'Drag and drop files here, or click to select files'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: PDF
                </Typography>
              </Paper>

              {files.length > 0 && (
                <Box sx={{ mt: 3, width: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Selected Files:
                  </Typography>
                  {files.map((file, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 1,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body1">{file.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#FFFFFF',
              py: 2
            }}>
              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#dee2e6' }}>
                <Typography variant="h6" gutterBottom>
                  Artifact Details
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  margin="normal"
                  sx={{ backgroundColor: '#FFFFFF' }}
                  required
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                  sx={{ backgroundColor: '#FFFFFF' }}
                  required
                />
              </Paper>

              <Paper sx={{ p: 3, mb: 3, backgroundColor: '#dee2e6' }}>
                <Typography variant="h6" gutterBottom>
                  Requirements
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="New Requirement"
                    value={newRequirement}
                    onChange={(e) => setNewRequirement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRequirement()}
                    sx={{ backgroundColor: '#FFFFFF' }}
                  />
                  <IconButton
                    onClick={handleAddRequirement}
                    sx={{ 
                      bgcolor: 'rgba(43, 84, 135, 0.62)',
                      color: '#FFFFFF',
                      '&:hover': {
                        bgcolor: 'rgba(43, 84, 135, 0.8)',
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                <List>
                  {requirements.map((req, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: 'rgba(43, 85, 135, 0.05)',
                        mb: 1,
                        borderRadius: 1,
                      }}
                    >
                      <ListItemText primary={req} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteRequirement(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          </TabPanel>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={tabValue === 0 ? files.length === 0 : (!title.trim() && !description.trim() && requirements.length === 0)}
            sx={{
              bgcolor: '#2B5587',
              color: '#FFFFFF',
              fontWeight: 500,
              px: 4,
              py: 1,
              fontSize: '1rem',
              '&:hover': {
                bgcolor: '#1a3a5f',
              },
              '&:active': {
                bgcolor: '#142d4a',
              },
              '&.Mui-disabled': {
                bgcolor: '#cccccc',
                color: '#666666',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Add Artifact
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AddArtifactPopup; 